import React, { useEffect, useMemo, useState } from "react";
import avatarIcon from "../../assets/icons/avatar.svg";
import CloseIcon from "@mui/icons-material/Close";
import { getTenantPlanDetails, getUserDetails } from "../../api/settings";
import { useNavigate } from "react-router-dom";
import { routes } from "../../constants/routes";
import { getCurrentUser } from "../../common/routines/user-operations";
import { useLogin } from "../../common/hooks/useUser";
import { useSetLoading } from "../../common/hooks/appearance";
import { useSelector } from "react-redux";
import { isValidPhoneNumber } from "../../utils/utils";

interface ProfileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileClick: () => void;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  isOpen,
  onClose,
  onProfileClick,
}) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [activePlan, setActivePlan] = useState<any>({});

  const currentUser = getCurrentUser();
  const userId = currentUser?.id;
  const planDetails = useLogin();
  const planName = planDetails?.tenantDetails?.subscriptionPlan?.name;
  const primaryColor = planDetails?.tenantDetails?.primaryColor;
  const accentColor = planDetails?.tenantDetails?.accentColor;

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await getUserDetails(userId);
      setProfile(response.data?.data?.data?.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getDetails = async () => {
    try {
      setLoading(true);
      const res = await getTenantPlanDetails();
      const data = res?.data?.data;
      setActivePlan({
        ...data?.activePlanDetails,
        plan_name: data?.activePlanDetails?.planName,
        reports_type: data?.activePlanDetails?.reportsType,
        plan_type: data?.activePlanDetails?.plan_type,
        id: data?.activePlanDetails?.subscription_plan_id,
      });

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };
  useEffect(() => {
    fetchUserProfile();
    getDetails();
  }, [userId]);

  const userInfo = profile?.find((user: any) => userId === user?.id);
  const profileUrl = useSelector(
    (state: any) => state?.session?.userDetails?.profile_url
  );

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  if (loading) {
    return (
      <div className={`profile-sidebar ${isOpen ? "open" : ""}`}>
        <div className="profile-header">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className={`profile-sidebar ${isOpen ? "open" : ""}`}>
        <div className="profile-header">
          <p>User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`profile-sidebar ${isOpen ? "open" : ""}`}>
      <div className="profile-header">
        <img
          src={profileUrl || avatarIcon}
          alt="Profile"
          className="profile-avatar"
        />
        <h3 className="profile-name" onClick={onProfileClick}>
          {userInfo?.first_name + " " + userInfo?.last_name}
        </h3>
        <span className="profile-role">{userInfo?.role?.role}</span>
      </div>
      <div className="contact-details">
        <div className="contact-info">
          <p>Email</p>
          <p>{userInfo?.email || "Not available"}</p>
        </div>
        <div className="contact-info">
          <p>Phone</p>
          <p>
            {isValidPhoneNumber(userInfo?.phone_number)
              ? userInfo?.phone_number
              : "Not Available"}
          </p>
        </div>
      </div>
      <div className="plan-details">
        <h3>Current Plan</h3>
        <div className="current-plan">
          <div className="plan-name">
            <p>{activePlan?.plan_name || "-"}</p>
          </div>
          <button
            className="upgrade-button"
            onClick={() => {
              navigate(routes.USER_CHANGE_PLAN);
            }}
          >
            Upgrade
          </button>
        </div>
      </div>
      <div className="profile-theme">
        <p>Theme</p>
        <div className="theme-options">
          <div className="theme-button">
            <div
              className="color-preview"
              style={{
                backgroundColor: primaryColor || "#887952",
                width: "14px",
                height: "14px",
                borderRadius: "4px",
                marginRight: "8px",
              }}
            />
            <span>Primary Color</span>
          </div>

          <div className="theme-button">
            <div
              className="color-preview"
              style={{
                backgroundColor: accentColor || "#726545",
                width: "14px",
                height: "14px",
                borderRadius: "4px",
                marginRight: "8px",
              }}
            />
            <span>Accent Color</span>
          </div>
        </div>
      </div>
      <button className="closeButton" type="button" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </button>
    </div>
  );
};

export default ProfileSidebar;
